'use client';

import { useTransition } from 'react';
import { useLocale } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { writeLanguageCookie } from '@workspace/i18n';
import {
    buildLocalizedPath,
    DEFAULT_LOCALE,
    isSupportedLocale,
    stripEnterMarketplacePrefix,
    type EnterHomeLocale,
} from '@/contexts/i18n/utils';

export function useHomeLanguage() {
    const locale = useLocale();
    const router = useRouter();
    const pathname = usePathname();
    const [isPending, startTransition] = useTransition();

    function switchLanguage(target: EnterHomeLocale): void {
        if (isPending || target === locale) return;
        const href = buildLocalizedPath(target, pathname) + window.location.search + window.location.hash;
        // 先同步偏好，直接使用公开 URL，避免 next-intl 强制生成临时 /en 前缀。
        writeLanguageCookie(target);
        startTransition(() => router.push(href, { scroll: false }));
    }

    return { locale, isPending, switchLanguage };
}

export function useLocalizedHref(pathname: string): string {
    const locale = useLocale();
    return buildLocalizedPath(isSupportedLocale(locale) ? locale : DEFAULT_LOCALE, pathname);
}

export function useLocalizedNavigation(): {
    href: (pathname: string) => string;
    push: (pathname: string) => void;
    assign: (pathname: string) => void;
} {
    const locale = useLocale();
    const router = useRouter();
    const pathname = usePathname();
    const resolvedLocale = isSupportedLocale(locale) ? locale : DEFAULT_LOCALE;
    const productPrefixed = stripEnterMarketplacePrefix(pathname).prefixed;
    const href = (targetPathname: string) => buildLocalizedPath(resolvedLocale, targetPathname, { productPrefixed });

    return {
        href,
        push: (targetPathname: string) => router.push(href(targetPathname)),
        // 目标页由其它应用承载时必须整页跳转：push 的软导航只能在 Home 内完成，
        // 对方应用的重定向会把浏览器带到它的内部地址。
        assign: (targetPathname: string) => window.location.assign(href(targetPathname)),
    };
}
